/**
 * <video-modal> — play-to-video dialog.
 *
 * Wraps a trigger button and a native <dialog> (see snippets/video-play.liquid).
 * Clicking the trigger opens the dialog and plays the video; ESC, the backdrop,
 * and the close button dismiss it. On close, playback stops and focus returns to
 * the trigger so audio never continues behind the page. External URLs (YouTube /
 * Vimeo / direct file) are embedded lazily on first open and cleared on close.
 *
 * Defined once, guarded so a double include never re-defines the element.
 */
if (!customElements.get('video-modal')) {
  class VideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-video-dialog]');
      this.trigger = this.querySelector('[data-video-open]');
      if (!this.dialog || !this.trigger || this._bound) return;
      this._bound = true;

      this.closeBtn = this.querySelector('[data-video-close]');
      this.media = this.querySelector('video');
      this.embed = this.querySelector('[data-video-embed]');

      this.trigger.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

      // Backdrop click (the dialog element itself, not its inner content).
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      // Native close (ESC, .close()) → stop playback + restore focus.
      this.dialog.addEventListener('close', () => this.stop());
    }

    open() {
      if (this.embed && !this.embed.dataset.loaded) {
        this.embed.innerHTML = this.buildEmbed(this.embed.dataset.embedSrc);
        this.embed.dataset.loaded = '1';
      }
      this.dialog.showModal();
      if (this.media) {
        const promise = this.media.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    stop() {
      if (this.media) {
        this.media.pause();
        try {
          this.media.currentTime = 0;
        } catch (error) {
          /* some sources don't allow seeking before metadata loads */
        }
      }
      if (this.embed && this.embed.dataset.loaded) {
        this.embed.innerHTML = '';
        delete this.embed.dataset.loaded;
      }
      if (this.trigger) this.trigger.focus();
    }

    buildEmbed(url) {
      if (!url) return '';
      const title = this.dialog.getAttribute('aria-label') || 'Video';
      const iframe = (src) =>
        '<iframe class="video-modal__iframe" src="' +
        src +
        '" title="' +
        title.replace(/"/g, '&quot;') +
        '" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';

      const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
      if (yt) return iframe('https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0');

      const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vimeo) return iframe('https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1');

      // Fall back to a direct media file.
      return (
        '<video class="video-modal__media" controls autoplay playsinline src="' +
        url.replace(/"/g, '&quot;') +
        '"></video>'
      );
    }
  }

  customElements.define('video-modal', VideoModal);
}
